/**
 * What an onboarding or an offboarding says the moment it has started: who was
 * told, which departments have nobody to do their part (grouped, not one line
 * per card), and the other things worth knowing. Shared by both start screens.
 */

import type { ReactNode } from "react";
import type { StartResponse } from "@utuwrk/core";
import { mayOpen } from "../lib/access.js";
import { useOrg } from "../lib/org.js";
import { plural } from "../lib/words.js";
import { Link } from "./bits.js";

export function Started({
  lede,
  started,
  openBoard,
  boards,
}: {
  /** What was done at the start, in one sentence; replaced by the unstaffed warning where it would be untrue. */
  lede: ReactNode;
  started: Pick<StartResponse, "notes" | "unstaffed">;
  openBoard: () => void;
  /** A cohort's boards, one link each, in place of the one button. */
  boards?: { id: string; name: string }[];
}) {
  const { org } = useOrg();
  // The same rule as the menus; Settings does not look at the platform-admin flag.
  const maySet = mayOpen({ name: "settings" }, org.role, false);
  const { unstaffed, notes } = started;
  const untold = unstaffed.reduce((sum, group) => sum + group.cards.filter((card) => card.open).length, 0);

  return (
    <div className="panel started">
      <p className="panel-lede">
        {untold > 0 ? "Everything else opens as the work before it finishes." : lede}
      </p>

      {unstaffed.length > 0 ? (
        <section className="gap-card" aria-labelledby="gap-title">
          <header className="gap-head">
            <svg className="gap-icon" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M8 2.2 14.2 13H1.8L8 2.2ZM8 6.4v3M8 11.3v.1" />
            </svg>
            <div>
              <h2 id="gap-title" className="gap-title">
                {untold > 0
                  ? `${plural(untold, "open card has", "open cards have")} nobody to do ${untold === 1 ? "it" : "them"}`
                  : "Some departments have nobody set yet"}
              </h2>
              <p className="gap-lede">
                {untold > 0
                  ? "Nobody has been told about those. Name an owner and they will be."
                  : "Nobody is told until their card opens. Name an owner before then."}
              </p>
            </div>
          </header>
          <ul className="gaps">
            {unstaffed.map((group) => {
              const now = group.cards.filter((card) => card.open).length;
              return (
                <li key={group.department}>
                  <details open={now > 0}>
                    <summary>
                      <span className="gap-dept">{group.department}</span>
                      <span className="chip">{plural(group.cards.length, "card")}</span>
                      {now > 0 ? <span className="pill s-warn">{now} open now</span> : null}
                    </summary>
                    <ul className="gap-cards">
                      {group.cards.map((card) => (
                        <li key={card.key}>
                          <span>{card.title}</span>
                          {card.open ? <span className="gap-tag">Open now</span> : null}
                        </li>
                      ))}
                    </ul>
                  </details>
                </li>
              );
            })}
          </ul>
          {maySet ? (
            <Link to={{ name: "settings" }} className="button button-quiet">
              Set who does each part
            </Link>
          ) : null}
        </section>
      ) : null}

      {notes.length > 0 ? (
        <section className="worth-knowing" aria-labelledby="worth-title">
          <h2 id="worth-title" className="section-title">
            Worth knowing
          </h2>
          <ul className="facts">
            {notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {boards ? (
        <ul className="facts" aria-label="Their boards">
          {boards.map((board) => (
            <li key={board.id}>
              <Link to={{ name: "board", id: board.id }}>{board.name}</Link>
            </li>
          ))}
        </ul>
      ) : (
        <div className="action-row">
          <button className="button" type="button" onClick={openBoard}>
            Open the board
          </button>
        </div>
      )}
    </div>
  );
}
